import { createAction } from 'redux-actions';
import { store } from 'store/createStore';
import _ from 'lodash';
import API from '../../../../../utils/api';

export const setCheckingMobileConnection = createAction(
  '@@channelHost/setCheckingMobileConnection',
  value => () => {
    return value;
  }
);

export const fetchChannelEvent = createAction(
  '@@channelHost/fetchChannelEvent',
  eventId => () => {
    const params = {
      populate: 'channel host game',
      query: JSON.stringify({ active: true }),
    };

    return API.getChannelEvent(eventId, params).then(({ data }) => data);
  }
);

const isHost = () => {
  const event = _.get(store.getState(), 'channelHost.event', {});

  if (
    event &&
    event.host &&
    event.host._id.toString() === store.getState().auth.user._id.toString()
  ) {
    return true;
  }
  return false;
};

const getTemplatesList = templatesList => {
  const auxList = [];
  for (const temp of templatesList) {
    auxList.push(temp._id.toString());
  }

  return auxList;
};

export const fetchEventTemplates = createAction(
  '@@channelHost/fetchEventTemplates',
  () => () => {
    const event = _.get(store.getState(), 'channelHost.event', {});
    const templates = getTemplatesList(event.templates);

    return API.getTemplates({
      checkInteractionsAndPass: true,
      select: 'name createdAt updatedAt type params.category',
      eventId: event._id,
      query: JSON.stringify({ _id: { $in: templates } }),
    }).then(({ data }) => (data ? data.docs : null));
  }
);

export const fetchInteractions = createAction(
  '@@channelHost/fetchInteractions',
  showClosed => () => {
    const event = _.get(store.getState(), 'channelHost.event', {});
    const query = { event: event._id };

    if (!showClosed) {
      query.active = true;
    }

    return API.getInteractions({
      select: 'name publishedAt type template active standalone params',
      query: JSON.stringify(query),
      interactionEngagement: true,
    }).then(({ data }) => {
      if (data && data.docs) {
        return data.docs;
      }
    });
  }
);

const getGameTeams = event => {
  const teams = { A: null, B: null };
  if (event && event.game && event.game._id) {
    const game = event.game;

    if (game && game.home && game.away) {
      if (game.homePosition === 'right') {
        teams.A = game.away;
        teams.B = game.home;
      } else {
        teams.A = game.home;
        teams.B = game.away;
      }
    }
  }

  return teams;
};

export const fetchImageLibraryList = createAction(
  '@@channelHost/fetchImageLibraryList',
  league => () => {
    const event = _.get(store.getState(), 'channelHost.event', null);

    let leagueSelected = null;

    if (!league && event && event.game && event.game._id) {
      const game = event.game;

      if (game.leagueId) {
        leagueSelected = game.leagueId;
      }
    } else {
      leagueSelected = league;
    }

    const teams = getGameTeams(event);

    return API.getImageLibraryList({
      leagueId: leagueSelected,
      teamAId: teams.A ? teams.A._id : null,
      teamBId: teams.B ? teams.B._id : null,
    }).then(({ data }) => data);
  }
);

export const fetchLeagues = createAction('@@channelHost/fetchLeagues', () => () => {
  return API.getLeagues({
    ignoreDependencies: true,
    select: 'name homePosition',
  }).then(({ data }) => {
    if (data && data.docs) {
      return data.docs;
    }
    return [];
  });
});

export const fetchTeams = createAction('@@channelHost/fetchTeams', leagueId => () => {
  if (leagueId) {
    let query = { leagues: leagueId };
    query = JSON.stringify(query);
    return API.getTeams({ query, ignoreDependencies: true }).then(({ data }) => {
      return data.docs || [];
    });
  }
  return [];
});

export const sendImage = createAction(
  '@@channelHost/sendImage',
  (imageUrl, image, imageContentType) => () => {
    if (isHost()) {
      const event = _.get(store.getState(), 'channelHost.event', {});

      const awayTeam = _.get(store.getState(), 'channelHost.event.game.away._id', null);

      const homeTeam = _.get(store.getState(), 'channelHost.event.game.home._id', null);
      const listConditions = _.get(store.getState(), 'channelHost.listConditions', []);
      const logicalConnector = _.get(
        store.getState(),
        'channelHost.logicalConnector.conector',
        'and'
      );
      const message = {
        type: 'image',
        channel: event.channel._id,
        event: event._id,
        body: {
          imageUrl: imageUrl,
          image: image,
          imageContentType,
        },
        awayTeam,
        homeTeam,
        listConditions,
        logicalConnector,
      };
      API.sendMessage(message);
    } else {
      return null;
    }
  }
);

export const sendText = createAction('@@channelHost/sendText', textMessage => () => {
  const event = _.get(store.getState(), 'channelHost.event', {});
  const awayTeam = _.get(store.getState(), 'channelHost.event.game.away._id', null);

  const homeTeam = _.get(store.getState(), 'channelHost.event.game.home._id', null);
  const listConditions = _.get(store.getState(), 'channelHost.listConditions', []);
  const logicalConnector = _.get(
    store.getState(),
    'channelHost.logicalConnector.conector',
    'and'
  );

  const message = {
    type: 'text',
    channel: event.channel._id,
    event: event._id,
    body: {
      text: textMessage.trim(),
    },
    awayTeam,
    homeTeam,
    listConditions,
    logicalConnector,
  };

  return API.sendMessage(message);
});

export const closeInteraction = createAction(
  '@@channelHost/closeInteraction',
  row => () => {
    if (isHost()) {
      API.stopInteraction({ id: row._id, notify: true });
    } else {
      return null;
    }
  }
);

export const changeChannelSocket = createAction('@@channelHost/changeChannelSocket');

export const updateClientsMobileConnected = createAction(
  '@@channelHost/updateClientsMobileConnected',
  users => () => {
    return users;
  }
);

export const updateListClientsConnected = createAction(
  '@@channelHost/updateListClientsConnected',
  users => () =>
    API.getUserInteractions({ users }).then(({ data }) => {
      if (data && data.docs) {
        return data.docs;
      }
    })
);

export const copyToClipboard = createAction('@@channelHost/copyToClipboard');

export const fetchInteractionAnalytics = createAction(
  '@@channelHost/fetchInteractionAnalytics',
  interactionId => () =>
    API.getInteractionStatistics({
      interaction: interactionId,
    }).then(({ data }) => data)
);

export const filterOption = createAction(
  '@@channelHost/filterOption',
  (interaction, question, option) => async () => {
    const adminState = store.getState().channelAdmin;
    const users = _.get(
      adminState,
      `interactionAnalytics.data.${question.questionIdx}.options.${option.index}.users`,
      []
    );

    if (users) {
      return API.getUserInteractions({ users }).then(({ data }) => {
        if (data && data.docs) {
          return data.docs;
        }
      });
    }
  }
);

export const sendStatistics = createAction(
  '@@channelHost/sendStatistics',
  arrayObjChart => () => {
    if (isHost()) {
      const event = _.get(store.getState(), 'channelHost.event', {});
      const awayTeam = _.get(store.getState(), 'channelHost.event.game.away._id', null);
      const homeTeam = _.get(store.getState(), 'channelHost.event.game.home._id', null);

      const listConditions = _.get(store.getState(), 'channelHost.listConditions', []);
      const logicalConnector = _.get(
        store.getState(),
        'channelHost.logicalConnector.conector',
        'and'
      );

      const message = {
        type: 'statistics',
        channel: event.channel._id,
        event: event._id,
        body: {
          arrayObjChart: arrayObjChart,
        },
        awayTeam,
        homeTeam,
        listConditions,
        logicalConnector,
      };

      return API.sendMessage(message);
    } else {
      return null;
    }
  }
);

export const onCloseDialogAnalytics = createAction(
  '@@channelHost/onCloseDialogAnalytics'
);

export const closeDataListView = createAction('@@channelHost/closeDataListView');

export const closeEvent = createAction('@@channelHost/closeEvent', eventId => () => {
  if (isHost()) {
    API.stopChannelEvent(eventId);
  } else {
    return null;
  }
});

export const addTagCondition = createAction(
  '@@channelHost/addTagCondition',
  (keyId, keyName, operator, valueId, valueName) => () => {
    return { keyId, keyName, operator, valueId, valueName };
  }
);

export const removeTagCondition = createAction(
  '@@channelHost/removeTagCondition',
  idx => () => {
    return idx;
  }
);

export const clearTagCondition = createAction('@@channelHost/clearTagCondition');

export const setLogicalConnector = createAction(
  '@@channelHost/setLogicalConnector',
  value => () => {
    return value;
  }
);
